import * as FileSaver from "file-saver";
import { default as React, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Dropdown,
  DropdownButton,
  Form,
  InputGroup,
  OverlayTrigger,
  Row,
  Table,
  Tooltip
} from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import cellEditFactory from "react-bootstrap-table2-editor";
import { MdKeyboardArrowLeft } from "react-icons/md";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import LoaderButton from "../components/LoaderButton";
import PageLoader from "../components/PageLoader";
import StockPriceDisplay from "../components/StockPriceDisplay";
import {
  calculateTerm,
  calculateTermsInDays,
  convertAndPerformCalculation,
  getFormulae
} from "../libs/blackscholes";
import { Constants } from "../libs/Constants";
import { get, getList, update } from "../libs/dbFunctions";
import { validateForms } from "../libs/hooksLib";
import { loadRecentStockPrice } from "../libs/stockPricesLib";
import {
  formatCurrency,
  formatDate,
  getCurrentDate,
  incrementColumnForExcel
} from "../libs/utils";
import { getConfig } from "../Objects/Object";
import "./BlackScholes.scss";
const moment = require("moment");
const fileType =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
const fileExtension = ".xlsx";

export default function BlackScholes(props) {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState(null);
  const [fields, setFields] = useState({ rows: [] });
  const [config, setConfig] = useState(null);
  const [outputs, setOutputs] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [hasBlackSchole, setHasBlackSchole] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [plan, setPlan] = useState(null);
  const [rules, setRules] = useState(null);
  const [showCalculate, setShowCalculate] = useState(true);
  const [showSavebutton, setShowSavebutton] = useState(true);
  const [newBlackschole, setNewBlackschole] = useState(false);
  const [showOutputs, setShowOutputs] = useState(true);
  const [recentStockPrice, setRecentStockPrice] = useState({});

  function handleChange(e) {
    let o = Object.assign({}, fields, {
      [e.target.id]:
        e.target.type === "number" && e.target.value
          ? parseFloat(e.target.value)
          : e.target.value,
    });

    setErrors(validateForms(o, config.validationSchema, config.fieldTypes));

    let newRows = [];
    for (let r of fields.rows) {
      let nw = Object.assign({}, r, {
        stock_price: o.stockPrice,
        exercise_price: o.exercise_price || o.stockPrice,
        volatility: o.volatility,
      });
      newRows.push(nw);
    }

    setFields({
      ...o,
      rows: newRows,
    });
  }

  function updateField(row) {
    let newRows = [];
    for (let r of fields.rows) {
      let nw = { ...r };
      if (row.id === r.id) {
        nw = Object.assign({}, r, {
          exercise_price: row.exercise_price,
          term: calculateTerm(row.termInDays),
          discount_rate: row.discount_rate,
          arqd: row.arqd,
        });
      }
      newRows.push(nw);
    }

    setFields({
      ...fields,
      rows: newRows,
    });
  }

  useEffect(() => {
    let config = getConfig(Constants.dbTypes.BLACK_SCHOLES);
    setConfig(config);
  }, [props.isAuthenticated, props.userObj]);

  useEffect(() => {
    async function fetchStockPrice() {
      try {
        let data = await loadRecentStockPrice(props.activeCompany.code);
        setRecentStockPrice(data);
      } catch (e) {
        toast.error(e);
      }
    }
    fetchStockPrice();
  }, [props.activeCompany.code]);

  useEffect(() => {
    async function load() {
      if (config) {
        setIsLoading(true);
        await getPlan();
        await getPeriods();
        setIsLoading(false);
      }
    }
    load();
  }, [config]);

  useEffect(() => {
    setIsLoading(true);
    if (fields && fields.rows.length > 0) {
      loadOutput();
    }
  }, [fields]);
  useEffect(() => {
    if (periods && periods.length > 0) {
      setDefaultPeriod();
    }
  }, [periods]);
  useEffect(() => {
    if (selectedPeriod && plan) {
      loadBlackSchole();
    }
  }, [selectedPeriod, plan, periods]);
  async function getPlan() {
    try {
      const planDetail = await getList(
        Constants.dbTypes.PLAN_DETAILS,
        props.activeCompany
      );
      if (planDetail && planDetail.length > 0) {
        let p = Object.assign(
          { ...planDetail[0] },
          {
            trancheCount:
              Number(planDetail[0].lookBack) /
              Number(planDetail[0].purchasePeriod),
            purchasePeriod: Number(planDetail[0].purchasePeriod),
          }
        );
        setPlan(p);
      }
    } catch (e) {
      toast.error(e);
    }
  }
  async function getPeriods() {
    //fetch the period rows from database
    const periodDetails = await getList(
      Constants.dbTypes.OFFERING_PERIOD,
      props.activeCompany
    );
    if (periodDetails && periodDetails.length > 0) {
      setPeriods(periodDetails);
    } else setPeriods([]);
  }
  function setDefaultPeriod() {
    if (periods && periods.length > 0) {
      let currentPeriod = { ...periods[0] };
      periods.map((period) => {
        if (
          getCurrentDate(false).isBetween(
            moment.utc(period.offerStartdate),
            moment.utc(period.offerEnddate)
          )
        ) {
          currentPeriod = Object.assign(period);
        }
      });
      setSelectedPeriod(currentPeriod);
    }
  }

  // This function is used to save black sholes
  async function handleSave(event) {
    event.preventDefault();
    setIsLoading(true);
    let saveFields = Object.assign(
      {},
      {
        companyId: props.activeCompany.id,
        periodStartdate: selectedPeriod.offerStartdate,
        stockPrice: fields.stockPrice,
        volatility: fields.volatility,
      }
    );
    let i = 0;
    for (let r of fields.rows) {
      saveFields[`tranche${++i}`] = {
        exercisePrice: r.exercise_price,
        term: r.term,
        volatility: r.volatility,
        annualRateQuarterlyDiv: r.arqd,
        discountRateBEY: r.discount_rate,
      };
    }
    if (props.activeCompany.id === Constants.TaablAdminCompany) {
      toast.error("Not allowed to create ESPP for TaablAdmin Company");
      setIsLoading(false);
    } else {
      try {
        await update(Constants.dbTypes.BLACK_SCHOLES, saveFields);
        props.history.push("/");
      } catch (e) {
        if (e.response && e.response.data && e.response.data.error) {
          toast.error(e.response.data.error);
        } else {
          toast.error(e);
        }
        setIsLoading(false);
      }
    }
  }

  async function loadBlackSchole() {
    setIsLoading(true);
    setHasBlackSchole(false);
    let id = {
      companyId: props.activeCompany.id,
      startDate: selectedPeriod.offerStartdate,
    };
    const periodsSelected = await get(Constants.dbTypes.PERIOD, id);
    if (
      "blackSchole" in periodsSelected &&
      periodsSelected.blackSchole.length > 0
    ) {
      if (periodsSelected.blackSchole[0]?.stockPrice !== 0)
        setHasBlackSchole(true);
      loadFields(periodsSelected.blackSchole[0]);
    } else {
      setHasBlackSchole(false);
      loadDefaultFields();
    }
    setIsLoading(false);
  }
  function loadDefaultFields() {
    let timezone = props.activeCompany.timezone;
    let newRows = [];
    let startDate = moment().tz(timezone);
    let endDate = moment()
      .tz(timezone)
      .add(plan.purchasePeriod, "months");
    // Selected period is not in past
    if (selectedPeriod && selectedPeriod.offerStartdate > startDate.valueOf()) {
      endDate = moment
        .tz(selectedPeriod.offerStartdate, timezone)
        .add(plan.purchasePeriod, "months");
    }

    for (let i = 0; i < plan.trancheCount; i++) {
      let days = Math.abs(endDate.diff(startDate, "days"));
      endDate = endDate.add(plan.purchasePeriod, "months");
      newRows.push({
        id: `${(i + 1) * plan.purchasePeriod} Months`,
        stock_price: recentStockPrice.displayPrice || 1,
        exercise_price: recentStockPrice.displayPrice || 1,
        termInDays: days,
        term: calculateTerm(days),
        volatility: 55,
        arqd: 0,
        discount_rate: 2.05,
      });
    }
    setFields({ ...config.empty, rows: newRows });
  }
  function loadOutput() {
    if (!newBlackschole) {
      let tranche = {};
      for (let i = 0; i < plan.trancheCount; i++) {
        tranche[`tranche${i + 1}`] = convertAndPerformCalculation(
          fields.rows[i]
        );
      }
      setOutputs(tranche);
      let rule = getFormulae();
      setRules(rule);
      setShowOutputs(true);
      setShowCalculate(false);
    }
    setIsLoading(false);
  }
  async function handlePeriodSelect(e, period) {
    setIsLoading(true);
    setSelectedPeriod(period);
    setShowOutputs(true);
    setShowSavebutton(true);
    setIsLoading(false);
  }

  function loadFields(input) {
    let o = {
      ...fields,
      stockPrice: input.stockPrice,
      volatility: input.volatility,
    };
    let newRows = [];
    for (let key in input) {
      if (key.startsWith("tranche")) {
        let i = parseInt(key.replace("tranche", ""));
        newRows.push({
          id: `${i * plan.purchasePeriod} Months`,
          tranche: i,
          stock_price: input.stockPrice,
          exercise_price: input[key]?.exercisePrice || input.stockPrice,
          volatility: input.volatility,
          term: input[key].term,
          termInDays: calculateTermsInDays(input[key].term),
          arqd: input[key].annualRateQuarterlyDiv,
          discount_rate: input[key].discountRateBEY,
        });
      }
    }
    // Sort it by tranche
    newRows.sort((a, b) => a.tranche - b.tranche);
    setFields({ ...o, rows: newRows });
  }

  function handleCompute() {
    if (!newBlackschole) {
      setShowSavebutton(true);
      setShowCalculate(false);
    }
    let tranche = {};
    for (let i = 0; i < 4; i++) {
      tranche[`tranche${i + 1}`] = convertAndPerformCalculation(fields.rows[i]);
    }
    setOutputs(tranche);
    let rule = getFormulae();
    setRules(rule);
    setShowOutputs(true);
  }

  const columns = [
    {
      dataField: "id",
      text: "Tranche #",
    },
    {
      dataField: "stock_price",
      text: "Stock Price ($)",
      type: "number",
      editable: false,
      formatter: (cell) => {
        return `${formatCurrency(cell)}`;
      },
      classes: (cell, row, rowIndex, colIndex) => {
        return "bs-non-editable";
      },
    },
    {
      dataField: "exercise_price",
      text: "Exercise Price ($)",
      type: "number",
      editable: true,
      formatter: (cell) => {
        return <div>{formatCurrency(cell)} </div>;
      },
      classes: (cell, row, rowIndex, colIndex) => {
        return "bs-editable";
      },
    },
    {
      dataField: "termInDays",
      text: "Term in Days",
      type: "number",
      formatter: (cell) => {
        return <div>{cell} </div>;
      },
      classes: (cell, row, rowIndex, colIndex) => {
        return "bs-editable";
      },
      validator: (newValue, row, column) => {
        if (isNaN(newValue)) {
          return {
            valid: false,
            message: "Term in Days should be numeric",
          };
        }
        return true;
      },
    },
    {
      dataField: "term",
      text: "Term",
      editable: false,
      type: "number",
      formatter: (cell) => {
        return <div>{cell} </div>;
      },
      classes: (cell, row, rowIndex, colIndex) => {
        return "bs-non-editable";
      },
      validator: (newValue, row, column) => {
        if (isNaN(newValue)) {
          return {
            valid: false,
            message: "Term should be numeric",
          };
        }
        if (newValue > 100) {
          return {
            valid: false,
            message: "Term should be less than 100",
          };
        }
        return true;
      },
    },
    {
      dataField: "volatility",
      text: "Volatility (%)",
      type: "number",
      editable: false,
      formatter: (cell) => {
        return `${cell}%`;
      },
      classes: (cell, row, rowIndex, colIndex) => {
        return "bs-non-editable";
      },
    },
    {
      dataField: "arqd",
      text: "Annual Rate of Quarterly Dividends",
      type: "number",

      formatter: (cell) => {
        return <div>{cell}% </div>;
      },
      classes: (cell, row, rowIndex, colIndex) => {
        return "bs-editable";
      },
      validator: (newValue, row, column) => {
        if (isNaN(newValue)) {
          return {
            valid: false,
            message: "Annual Rate should be numeric",
          };
        }
        if (newValue > 100) {
          return {
            valid: false,
            message: "Annual Rate should be less than 100",
          };
        }
        return true;
      },
    },
    {
      dataField: "discount_rate",
      text: "Discount Rate - Bond Equivalent Yield (%)",
      type: "number",

      formatter: (cell) => {
        return <div>{cell}%</div>;
      },
      classes: (cell, row, rowIndex, colIndex) => {
        return "bs-editable";
      },
      validator: (newValue, row, column) => {
        if (isNaN(newValue)) {
          return {
            valid: false,
            message: "Discount Rate should be numeric",
          };
        }
        if (newValue > 100) {
          return {
            valid: false,
            message: "Discount Rate should be less than 100",
          };
        }
        return true;
      },
    },
  ];

  function exportToFile() {
    let sheet_rows = [
      { A: props.activeCompany.name },
      { A: "ESPP Black Scholes Calculations" },
      { A: "" },
      {
        A: "Share price on IPO date (first offering date)",
        B: `$ ${fields.stockPrice}`,
      },
      { A: "" },
      { A: "" },
      {},
      { A: "" },
      {},
    ];

    let char = "A";
    // Create a row for tranche
    let header_columns = [];
    for (let i = 1; i <= fields.rows.length; i++) {
      header_columns.push(char);
      sheet_rows[6][char] = `Tranche #${i} (${i * plan.purchasePeriod} month)`;
      sheet_rows[8][char] = "INPUT VARIABLES";
      char = incrementColumnForExcel(char, 3);
    }

    let ignore_fields = ["id", "tranche", "termInDays"];
    let input_variable_rows = {};
    for (let c of columns) {
      char = "A";

      if (!ignore_fields.includes(c.dataField)) {
        let row = {};
        for (let i = 1; i <= fields.rows.length; i++) {
          row[char] = "";
          if (header_columns.includes(char)) {
            row[char] = c.text;
          }
          char = incrementColumnForExcel(char);
          row[char] = "";
          char = incrementColumnForExcel(char);
          row[char] = "";
          char = incrementColumnForExcel(char);
        }
        input_variable_rows[c.dataField] = row;
      }
    }

    char = "B";
    for (let r of fields.rows) {
      for (let col of Object.keys(r)) {
        if (!ignore_fields.includes(col)) {
          input_variable_rows[col][char] = r[col];
        }
      }
      char = incrementColumnForExcel(char, 3);
    }
    sheet_rows.push(...Object.values(input_variable_rows));

    let outputs_variables = [
      {
        title: "INTERMEDIATE COMPUTATIONS",
        key: "intermediate",
        rows: [
          {
            title: "Present Value of Stock Ex-dividend",
            key: "pved",
          },
          {
            title: "Present Value of Exercise Price",
            key: "pvee",
          },
          {
            title: "Cumulative Volatility",
            key: "cv",
          },
        ],
      },
      {
        title: "CALL OPTIONS",
        key: "callOptions",
        rows: [
          {
            title: "Proportion of Stock Present Value",
            key: "pspv",
          },
          {
            title: "Proportion of Exercise Price PV",
            key: "pepp",
          },
          {
            title: "Call Option Value",
            key: "value",
          },
        ],
      },
      {
        title: "PUT OPTIONS",
        key: "putOptions",
        rows: [
          {
            title: "Proportion of Stock Present Value",
            key: "pspv",
          },
          {
            title: "Proportion of Exercise Price PV",
            key: "pepp",
          },
          {
            title: "Put Option Value",
            key: "value",
          },
        ],
      },
      {
        title: "",
        key: "outputs",
        rows: [
          {
            title: "Estimated Call Option Fair Value",
            key: "ecofv",
          },
          {
            title: "Estimated Put Option Fair Value",
            key: "epofv",
          },
          {
            title: "Value of Discount",
            key: "value_discount",
          },
          {
            title: "Value of 85% Call Option",
            key: "value_85_call",
          },
          {
            title: "Value of 15 Put Option",
            key: "value_15_put",
          },
          {
            title: "Total Value per Share ",
            key: "total_value_per_share",
          },
        ],
      },
    ];

    for (let output of outputs_variables) {
      sheet_rows.push({ A: "" });
      let heading_row = {};
      for (let c of header_columns) {
        heading_row[c] = output.title;
      }
      sheet_rows.push(heading_row);

      for (let ich of output.rows) {
        let row = {};
        char = "B";
        let headerChar = "A";
        for (let i = 1; i <= fields.rows.length; i++) {
          row[headerChar] = ich.title;
          row[char] = outputs[`tranche${i}`][output.key][ich.key];
          char = incrementColumnForExcel(char, 3);
          headerChar = incrementColumnForExcel(headerChar, 3);
        }
        sheet_rows.push(row);
      }
    }

    let ws = XLSX.utils.json_to_sheet(sheet_rows, {
      header: [
        "A",
        "B",
        "C",
        "D",
        "E",
        "F",
        "G",
        "H",
        "I",
        "J",
        "K",
        "L",
        "M",
        "N",
        "O",
        "P",
        "Q",
        "R",
        "S",
        "T",
        "U",
        "V",
        "W",
        "X",
        "Y",
        "Z",
      ],
      skipHeader: true,
    });

    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, "blackScholes" + fileExtension);
  }

  function formatOutput(type, key, trancheNo) {
    let tagKey = trancheNo + "." + type + "." + key;
    if (key === "cv") {
      return (
        <OverlayTrigger
          key={tagKey}
          placement={"top"}
          overlay={<Tooltip id={`tooltip-top`}>{rules[type][key]}</Tooltip>}
        >
          <td className="tbContent">
            {Math.round(outputs[trancheNo][type][key])}%{" "}
          </td>
        </OverlayTrigger>
      );
    }
    if (type === "outputs") {
      return (
        <OverlayTrigger
          key={tagKey}
          placement={"top"}
          overlay={<Tooltip id={`tooltip-top`}>{rules[type][key]}</Tooltip>}
        >
          <td className="tbContent">
            {formatCurrency(outputs[trancheNo][type][key])}{" "}
          </td>
        </OverlayTrigger>
      );
    } else {
      return (
        <OverlayTrigger
          key={tagKey}
          placement={"top"}
          overlay={<Tooltip id={`tooltip-top`}>{rules[type][key]}</Tooltip>}
        >
          <td className="tbContent">
            {" "}
            {formatCurrency(outputs[trancheNo][type][key])}{" "}
          </td>
        </OverlayTrigger>
      );
    }
  }
  function handleNewBlackSchole() {
    setNewBlackschole(true);
    setHasBlackSchole(false);
    loadDefaultFields();
    setShowCalculate(true);
    setShowOutputs(false);
    setShowSavebutton(false);
  }
  function renderOutput() {
    return (
      <div className="mt5">
        <Table className="outputTable" id="output">
          <thead>
            <tr>
              <th className="tableHeadings"> Output Variables</th>
              {fields.rows?.map((r, index) => {
                return (
                  <th key={index} className="tableHeadings">
                    Tranche #{index + 1} ({r.id})
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {[
              {
                heading: "Present Value of Stock Ex-dividend",
                options: "intermediate",
                formula: "pved",
                parent: true,
                parentHeading: "Intermediate Computations",
              },
              {
                heading: "Present Value of Exercise Price",
                options: "intermediate",
                formula: "pvee",
              },
              {
                heading: "Cumulative Volatility",
                options: "intermediate",
                formula: "cv",
              },
              {
                heading: "Proportion of Stock Present Value",
                options: "callOptions",
                formula: "pspv",
                parent: true,
                parentHeading: "CALL OPTION",
              },
              {
                heading: "Proportion of Exercise Price PV",
                options: "callOptions",
                formula: "pepp",
              },
              {
                heading: "Call Option Value",
                options: "callOptions",
                formula: "value",
              },
              {
                heading: "Proportion of Stock Present Value",
                options: "putOptions",
                formula: "pspv",
                parent: true,
                parentHeading: "PUT OPTION",
              },
              {
                heading: "Proportion of Exercise Price PV",
                options: "putOptions",
                formula: "pepp",
              },
              {
                heading: "Call Option Value",
                options: "putOptions",
                formula: "value",
              },
              {
                heading: "Estimated Call Option Fair Value",
                options: "outputs",
                formula: "ecofv",
                parent: true,
                parentHeading: "OUTPUTS",
              },
              {
                heading: "Estimated Put Option Fair Value",
                options: "outputs",
                formula: "epofv",
              },
              {
                heading: "Value of Discount",
                options: "outputs",
                formula: "value_discount",
              },
              {
                heading: "Value of 85% Call Option",
                options: "outputs",
                formula: "value_85_call",
              },
              {
                heading: "Value of 15 Put Option",
                options: "outputs",
                formula: "value_15_put",
              },
              {
                heading: "Total Value per Share",
                options: "outputs",
                formula: "total_value_per_share",
              },
            ].map((r, index) => {
              return (
                <React.Fragment>
                  {r.parent && (
                    <tr key={index} className="tableHeadings">
                      <td colSpan={fields.rows.length + 1}>
                        {r.parentHeading}
                      </td>
                    </tr>
                  )}
                  <tr key={index}>
                    <td className="rowHeadings">{r.heading}</td>
                    {fields.rows?.map((row, index) => {
                      return formatOutput(
                        r.options,
                        r.formula,
                        `tranche${index + 1}`
                      );
                    })}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
        <div className="d-flex">
          {showSavebutton && (
            <LoaderButton
              size="lg"
              className="mt3"
              id="save"
              variant="primary"
              disabled={hasBlackSchole}
              onClick={handleSave}
              isLoading={isLoading}
            >
              Save
            </LoaderButton>
          )}
          <Button
            size="lg"
            variant="secondary"
            className="ms-3 mt3 fw-bold"
            onClick={exportToFile}
          >
            Export to XLS
          </Button>
        </div>
      </div>
    );
  }
  function renderLinkCreatePeriod() {
    return (
      <div>
        <Alert variant={"warning"}>
          There is no active Period for this Company. Click{" "}
          <Alert.Link
            href={`/editObj/${JSON.stringify({
              type: Constants.dbTypes.OFFERING_PERIOD,
            })}`}
          >
            create Period{" "}
          </Alert.Link>{" "}
          to create one.
        </Alert>
      </div>
    );
  }
  return (
    <PageLoader isActive={isLoading} individual={false}>
      <div className="container p5 font-secondary black-contrai" id="content">
        <Card className="row black-head">
          <Card.Body>
            <Card.Title as="h2" className="companies-drop-down mb8">
              Black Scholes
            </Card.Title>
            {!isLoading && (
              <div>
                {(!periods || Object.keys(periods).length === 0) &&
                  renderLinkCreatePeriod()}
                {periods && Object.keys(periods).length > 0 && (
                  <div>
                    <form>
                      {!newBlackschole && (
                        <div className="d-flex align-items-center m-0">
                          <DropdownButton
                            id="dropdownMenuButton"
                            size="lg"
                            className="btn btn-taabl"
                            title={`Period- ${formatDate(
                              selectedPeriod?.offerStartdate,
                              Constants.DATEFORMAT.MONYYYY
                            )}`}
                          >
                            {periods.map((period) => {
                              return (
                                <Dropdown.Item
                                  onClick={(e) => handlePeriodSelect(e, period)}
                                  key={period.offerStartdate}
                                >
                                  {formatDate(
                                    period.offerStartdate,
                                    Constants.DATEFORMAT.MONYYYY
                                  )}
                                </Dropdown.Item>
                              );
                            })}
                          </DropdownButton>

                          <div>
                            <Button
                              size="lg"
                              variant="secondary"
                              className="ms-3 fw-bold"
                              onClick={handleNewBlackSchole}
                            >
                              Run New Black Scholes
                            </Button>
                          </div>
                        </div>
                      )}
                      {newBlackschole && (
                        <div className="row m-0">
                          <div className="dropdown">
                            <Button
                              size="lg"
                              variant="primary"
                              className="ms-3 fw-bold"
                              href={`/blackScholes`}
                            >
                              Back <MdKeyboardArrowLeft />
                            </Button>
                            <Button
                              size="lg"
                              variant="secondary"
                              className="ms-3 fw-bold"
                              onClick={handleNewBlackSchole}
                            >
                              Run New Black Scholes
                            </Button>
                          </div>
                        </div>
                      )}
                      <Row className="mt-3">
                        <Col sm={6}>
                          <Form.Group
                            controlId="stockPrice"
                            className={`form-group`}
                          >
                            <Form.Label>
                              Share price on first date of offering period
                            </Form.Label>
                            <Form.Label className="pull-right">
                              <StockPriceDisplay
                                label="Recent Price:"
                                stockSymbol={props.activeCompany.code}
                              />
                            </Form.Label>

                            <InputGroup>
                              <InputGroup.Text id="basic-addon1">
                                USD
                              </InputGroup.Text>
                              <Form.Control
                                size="lg"
                                className="quantity border-right"
                                value={fields.stockPrice || ""}
                                placeholder={formatCurrency(
                                  recentStockPrice.displayPrice || 0
                                )}
                                type="number"
                                onChange={handleChange}
                                isInvalid={errors && errors.stockPrice}
                                readOnly={hasBlackSchole}
                              />
                              <Form.Control.Feedback type="invalid">
                                <span>{errors && errors.stockPrice} </span>
                              </Form.Control.Feedback>
                            </InputGroup>
                          </Form.Group>
                        </Col>
                        <Col sm={2}>
                          <Form.Group
                            controlId="volatility"
                            className={`form-group`}
                          >
                            <Form.Label>Volatility</Form.Label>

                            <InputGroup>
                              <Form.Control
                                size="lg"
                                value={fields.volatility}
                                className="quantity"
                                placeholder="100%"
                                type="number"
                                onChange={handleChange}
                                isInvalid={errors && errors.volatility}
                                readOnly={hasBlackSchole}
                              />
                              <InputGroup.Text id="basic-addon1">
                                %
                              </InputGroup.Text>
                              <Form.Control.Feedback type="invalid">
                                <span>{errors && errors.volatility} </span>
                              </Form.Control.Feedback>
                            </InputGroup>
                          </Form.Group>
                        </Col>
                      </Row>
                    </form>
                    {
                      <BootstrapTable
                        bootstrap4={true}
                        keyField="id"
                        classes="table-bs-input"
                        data={fields.rows}
                        caption="Variables"
                        columns={columns}
                        cellEdit={cellEditFactory({
                          mode: "click",
                          blurToSave: true,
                          afterSaveCell: (oldValue, newValue, row, column) => {
                            updateField(row);
                          },
                        })}
                      />
                    }
                    {showCalculate && (
                      <LoaderButton
                        size="lg"
                        className="mt3"
                        id="calculate"
                        variant="primary"
                        onClick={handleCompute}
                        disabled={errors && Object.keys(errors).length > 0}
                        isLoading={isLoading}
                      >
                        Calculate
                      </LoaderButton>
                    )}
                  </div>
                )}
                {outputs && showOutputs && renderOutput()}
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </PageLoader>
  );
}
